// WebsiteStatusModal.jsx
import { useState } from "react";
import { toast } from "react-toastify";
import { motion } from "framer-motion";
import { 
  CheckCircle, XCircle, Clock, Upload, X, 
  FileText, Loader2, ChevronDown 
} from "lucide-react";
import WebsiteApiService from "../../ApiServices/WebsiteApiService/WebsiteApiService";

const statusOptions = [
  { value: "GettingReady", label: "Getting Ready", icon: Clock, color: "text-amber-500" },
  { value: "Approved", label: "Approved", icon: CheckCircle, color: "text-emerald-500" },
  { value: "Disapproved", label: "Disapproved", icon: XCircle, color: "text-red-500" },
];

const WebsiteStatusModal = ({ website, onClose, onSuccess }) => {
  const [status, setStatus] = useState(website.status);
  const [reason, setReason] = useState("");
  const [file, setFile] = useState(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  const selectedStatus = statusOptions.find(opt => opt.value === status);

  const handleSubmit = async () => {
    if (status === "Disapproved" && !reason) {
      toast.error("Please provide a rejection reason");
      return;
    }

    const formData = new FormData();
    formData.append("Id", website.websiteId);
    formData.append("Status", status);
    if (status === "Disapproved") {
      formData.append("RejectionReason", reason);
      if (file) formData.append("RejectionAttachment", file);
    }

    setIsUpdating(true);
    try {
      await WebsiteApiService.ChangeStatus(formData);
      toast.success("Status updated successfully!");
      onSuccess();
      onClose();
    } catch (error) {
      toast.error("Failed to update status");
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <motion.div
      initial={{ scale: 0.95, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      exit={{ scale: 0.95, opacity: 0 }}
      transition={{ type: "spring", damping: 20 }}
      className="bg-white p-6 rounded-xl shadow-xl w-full max-w-md border border-gray-100"
    >
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-semibold text-gray-800">Update Website Status</h2>
        <button
          onClick={onClose}
          className="p-1 rounded-full hover:bg-gray-100 transition-colors"
        >
          <X size={20} className="text-gray-500" />
        </button>
      </div>

      <div className="space-y-6">
        <div className="relative">
          <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="w-full flex items-center justify-between p-3 border rounded-lg hover:border-gray-400 transition-colors"
          >
            <div className="flex items-center gap-2">
              {selectedStatus && (
                <>
                  <selectedStatus.icon className={`${selectedStatus.color} w-5 h-5`} />
                  <span>{selectedStatus.label}</span>
                </>
              )}
            </div>
            <ChevronDown 
              size={18} 
              className={`text-gray-500 transition-transform ${isOpen ? "rotate-180" : ""}`}
            />
          </button>
          
          {isOpen && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="absolute z-10 mt-1 w-full bg-white border rounded-lg shadow-lg overflow-hidden"
            >
              {statusOptions.map((option) => (
                <div
                  key={option.value}
                  onClick={() => {
                    setStatus(option.value);
                    setIsOpen(false);
                  }}
                  className={`flex items-center gap-2 p-3 cursor-pointer hover:bg-gray-50 transition-colors ${
                    status === option.value ? "bg-blue-50" : ""
                  }`}
                >
                  <option.icon className={`${option.color} w-5 h-5`} />
                  <span>{option.label}</span>
                </div>
              ))}
            </motion.div>
          )}
        </div>

        {status === "Disapproved" && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            transition={{ duration: 0.3 }}
            className="space-y-4 overflow-hidden"
          >
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Rejection Reason
              </label>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                rows={3}
                placeholder="Provide details about why the website was disapproved..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Attachment (optional)
              </label>
              <label className="flex flex-col items-center justify-center w-full p-6 border-2 border-dashed rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                <div className="flex flex-col items-center justify-center text-center">
                  <Upload size={24} className="text-gray-400 mb-2" />
                  <p className="text-sm text-gray-500">
                    {file ? (
                      <span className="flex items-center gap-1 text-blue-600">
                        <FileText size={16} />
                        {file.name}
                      </span>
                    ) : (
                      "Click to upload or drag and drop"
                    )}
                  </p>
                  <p className="text-xs text-gray-400 mt-1">
                    PDF, DOC, PNG up to 5MB
                  </p>
                </div>
                <input
                  type="file"
                  onChange={(e) => setFile(e.target.files[0])}
                  className="hidden"
                />
              </label>
            </div>
          </motion.div>
        )}
      </div>

      <div className="flex justify-end gap-3 mt-8">
        <motion.button
          whileHover={{ scale: 1.03 }}
          whileTap={{ scale: 0.97 }}
          onClick={onClose}
          disabled={isUpdating}
          className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
        >
          Cancel
        </motion.button>
        <motion.button
          whileHover={{ scale: 1.03 }}
          whileTap={{ scale: 0.97 }}
          onClick={handleSubmit}
          disabled={isUpdating}
          className={`px-4 py-2 rounded-lg text-white flex items-center gap-2 ${
            isUpdating ? "bg-blue-400" : "bg-blue-600 hover:bg-blue-700"
          } transition-colors disabled:cursor-not-allowed`}
        >
          {isUpdating ? (
            <>
              <Loader2 size={18} className="animate-spin" />
              Updating...
            </>
          ) : (
            <>
              <CheckCircle size={18} />
              Save Changes
            </>
          )}
        </motion.button>
      </div>
    </motion.div>
  );
};

export default WebsiteStatusModal;